import axios from "axios";
import { URL,BASE_URL, SUCCESS_RESULT } from "../Constants";
import cogoToast from "cogo-toast";
import { createAuthRequestInterceptor, createAuthResponseInterceptor } from "./authRefresh";

// Helper function to detect network/internet connection errors
const isNetworkError = (error) => {
  // Check if browser reports offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return true;
  }
  
  // Check error message for network-related keywords
  const errorMessage = error?.message?.toLowerCase() || '';
  const networkKeywords = ['network', 'failed to fetch', 'network request failed', 'connection', 'timeout'];
  if (networkKeywords.some(keyword => errorMessage.includes(keyword))) {
    return true;
  }
  
  // Check error code
  const errorCode = error?.code?.toLowerCase() || '';
  if (errorCode === 'err_network' || errorCode === 'econnaborted' || errorCode === 'etimedout') {
    return true;
  }
  
  // Check axios-specific patterns: no response but request exists (network failure)
  if (!error.response && error.request) {
    return true;
  }
  
  return false;
};

// Helper function to enhance error with network error message
const enhanceErrorWithNetworkDetection = (error) => {
  if (isNetworkError(error)) {
    // Create a new error object with the network error message

    const networkError = new Error('No internet connection. Please check your network connection and try again.');
    networkError.isNetworkError = true;
    networkError.originalError = error;
    cogoToast.error("No internet connection. Please check your network connection and try again.", {
      hideAfter: 10,
    });
    
    // Preserve axios error structure
    if (error.response) {
      networkError.response = error.response;
    }
    if (error.request) {
      networkError.request = error.request;
    }
    networkError.config = error.config;
    return networkError;
  }
  return error;
};

const api = axios.create({
  baseURL: URL,
});
const publicApi = axios.create({
  baseURL: URL,
});
const mobileAPi = axios.create({
  baseURL: BASE_URL+"/api/mobile/v1/",
});
const apiDownloader = axios.create({
  baseURL: URL,
});

const attachAuthInterceptors = (axiosInstance, onResponseError) => {
  axiosInstance.interceptors.request.use(createAuthRequestInterceptor(), (error) =>
    Promise.reject(error)
  );
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      try {
        return await createAuthResponseInterceptor(axiosInstance)(error);
      } catch (authError) {
        return Promise.reject(
          onResponseError ? onResponseError(authError) : authError
        );
      }
    }
  );
};

attachAuthInterceptors(mobileAPi, enhanceErrorWithNetworkDetection);
attachAuthInterceptors(apiDownloader, enhanceErrorWithNetworkDetection);
attachAuthInterceptors(api, enhanceErrorWithNetworkDetection);

// Public clients: network detection only (no login redirect)
publicApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(enhanceErrorWithNetworkDetection(error))
);

apiDownloader.interceptors.request.use(
  (config) => {
    config.credentials = true;
    config.exposedHeaders = ["Content-Disposition"];
    config.maxBodyLength = Infinity;
    config.responseType = "blob";
    return config;
  },
  (error) => Promise.reject(error)
);




const login_api = async (data) => {
  return api.post("login", data, { skipAuthRefresh: true }).then((res) => {
    return res;
  });
};

const logout_api = async () => {
  return api.get("logout", { skipAuthRefresh: true }).then((res) => {
    return res;
  });
};

const auth_me_api = async () => {
  return api.get("auth/me", { skipAuthRefresh: true }).then((res) => {
    return res;
  });
};

const listBNFAttendance = async (data) => {
  return mobileAPi.post("attendance/list-attendance", data).then((res) => {
    return res;
  });
};

const forgotPassword_api = async (data) => {
  return api.post("forgot-password", data).then((res) => {
    return res;
  });
};

const updatePassword_api = async (data) => {
  return api.post("update-forgotten-password", data).then((res) => {
    return res;
  });
};

const listAssignedHospitals = async () => {
  return api.get("hmis/list-my-hospitals").then((res) => {
    return res;
  });
};

const listAssignedHospitalRooms = async (data) => {
  return api.post("hmis/list-assigned-hospital-rooms", data).then((res) => {
    return res;
  });
};
const listHospitalRooms = async (data) => {
  return api.post("hmis/list-hospital-rooms", data).then((res) => {
    return res;
  });
};

const searchPatients = async (data) => {
  return api.post("hmis/search-patient-information", data).then((res) => {
    return res;
  });
};

const getTemplate = async (data) => {
  return api.post("hmis/get-template", data).then((res) => {
    return res;
  });
};

const savePatientInformation = async (data) => {
  return api.post("hmis/save-patient-information", data).then((res) => {
    return res;
  });
};
const saveVisit = async (data) => {
  return api.post("hmis/get-patient-visit-ticket", data).then((res) => {
    return res;
  });
};
const updateConsultationDetails = async (data) => {
  return api.post("hmis/update-consultation-details", data).then((res) => {
    return res;
  });
};

const getPatientRequests = async (data) => {
  return api.post("hmis/get-patient-service-requests", data).then((res) => {
    return res;
  });
};
const listHospitalRoomsx = async (data) => {
  return api.post("hmis/list-my-consultation-rooms", data).then((res) => {
    return res;
  });
};

const RequestLabTest = async (data) => {
  return api.post("hmis/create-lab-request", data).then((res) => {
    return res;
  });
};
const UpdateLabResult = async (data) => {
  return api.post("hmis/update-lab-results", data).then((res) => {
    return res;
  });
};

const getProjects = async () => {
  return api.get("projects/list-projects").then((res) => {
    return res;
  });
};

const updateComponentProjectApi = async ({ project_id, component_id, new_project_id }) => {
  return api
    .post(`projects/${project_id}/update-component-project`, {
      component_id,
      project_id: new_project_id,
    })
    .then((res) => res);
};
const getComponentTypes = async (data) => {
  return api.post("projects/list-component-types", data).then((res) => {
    return res;
  });
};
const CreateComponents = async (data) => {
  return api.post("projects/create-components", data).then((res) => {
    return res;
  });
};
const getComponents = async (data) => {
  return api.post("projects/list-components", data).then((res) => {
    return res;
  });
};
 
const getCycles = async (data) => {
  return api.post("projects/list-component-cycles", data).then((res) => {
    return res;
  });
};
const GetProjectBeneficiaries = async (data) => {
  return api.post("projects/list-beneficiaries", data).then((res) => {
    return res;
  });
};
const assignProjectComponents = async (data) => {
  return api.post("projects/assign-components", data).then((res) => {
    return res;
  });
};
const createComponentCycle = async (data) => {
  return api.post("projects/create-component-cycle", data).then((res) => {
    return res;
  });
};
const initSurvey = async (data) => {
  return api.post("surveys/new-survey-initialization", data).then((res) => {
    return res;
  });
};
const listSurveys = async (data) => {
  return api.get("surveys/list-my-surveys").then((res) => {
    return res;
  });
};
const cloneSurvey = async (data) => {
  return api.post("surveys/clone-survey", data).then((res) => {
    return res;
  });
};
const updateQuestionnaire = async (data) => {
  return api.post("surveys/update-survey-questionnaire", data).then((res) => {
    return res;
  });
};

const onboardEnumerators = async (data) => {
  return api.post("surveys/onboard-survey-enumerators", data).then((res) => {
    return res;
  });
};
const ListSurveyEnumerators = async (data) => {
  return api.post("surveys/list-survey-enumerators", data).then((res) => {
    return res;
  });
};

const getSurveyManagers = async (data) => {
  return api.post("surveys/list-survey-managers", data).then((res) => {
    return res;
  });
};
const SaveSurveyManagers = async (data) => {
  return api.post("surveys/save-survey-managers", data).then((res) => {
    return res;
  });
};

const UpdateSurveyStatus = async (data) => {
  return api.post("surveys/update-survey-status", data).then((res) => {
    return res;
  });
};
const ListSubmissions = async (data) => {
  return api.post("surveys/list-submissions", data).then((res) => {
    return res;
  });
};
const UpdateEnumeratorsStatus = async (data) => {
  return api.post("surveys/update/enumerators-status", data).then((res) => {
    return res;
  });
};

const ListEmployeeApi = async () => {
  return api.get("hrm/list-employees").then((res) => {
    return res;
  });
};

const ListStaffLightweightApi = async (data) => {
  return api.post("hrm/list-staff-lightweight", data).then((res) => {
    return res;
  });
};

const ListStaffDetailsApi = async (data) => {
  return api.post("hrm/list-staff-details", data).then((res) => {
    return res;
  });
};

const UpdateStaffStatusApi = async (data) => {
  return api.post("hrm/update-staff-status", data).then((res) => {
    return res;
  });
};

const UpdateStaffJoinedAtApi = async (data) => {
  return api.post("hrm/update-staff-joined-at", data).then((res) => {
    return res;
  });
};

// Staff File Types APIs
const createStaffFileTypeApi = async (data) => {
  return api.post("hrm/staff-file-types/create", data).then((res) => {
    return res;
  });
};

const listStaffFileTypesApi = async (data) => {
  return api.post("hrm/staff-file-types/list", data).then((res) => {
    return res;
  });
};

const getStaffFileTypeDetailsApi = async (data) => {
  return api.post("hrm/staff-file-types/details", data).then((res) => {
    return res;
  });
};

const updateStaffFileTypeApi = async (data) => {
  return api.post("hrm/staff-file-types/update", data).then((res) => {
    return res;
  });
};

const deleteStaffFileTypeApi = async (data) => {
  return api.post("hrm/staff-file-types/delete", data).then((res) => {
    return res;
  });
};

// Staff Files APIs
const uploadStaffFileApi = async (formData) => {
  return api.post("hrm/staff-files/upload", formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }).then((res) => {
    return res;
  });
};

const listStaffFilesApi = async (data) => {
  return api.post("hrm/staff-files/list", data).then((res) => {
    return res;
  });
};

const downloadStaffFileApi = async (data) => {
  return apiDownloader.post("hrm/staff-files/download", data).then((res) => {
    return res;
  });
};

const deleteStaffFileApi = async (data) => {
  return api.post("hrm/staff-files/delete", data).then((res) => {
    return res;
  });
};

// Service Accounts API
const listServiceAccountsApi = async (data) => {
  return api.post("service-accounts/list", data).then((res) => {
    return res;
  });
};

const createServiceAccountApi = async (data) => {
  return api.post("service-accounts/create", data).then((res) => {
    return res;
  });
};

const regenerateServiceAccountSecretApi = async (data) => {
  return api.post("service-accounts/regenerate-secret", data).then((res) => {
    return res;
  });
};

// Staff Payslips API
const listStaffPayslipsApi = async (data) => {
  return api.post("hrm/payroll/list-staff-payslips", data).then((res) => {
    return res;
  });
};

// Scoring Periods API
const createScoringPeriodApi = async (data) => {
  return api.post("hrm/scoring-periods/create", data).then((res) => {
    return res;
  });
};

const listScoringPeriodsApi = async (data) => {
  return api.post("hrm/scoring-periods/list", data || {}).then((res) => {
    return res;
  });
};

const updateScoringPeriodApi = async (data) => {
  return api.post("hrm/scoring-periods/update", data).then((res) => {
    return res;
  });
};

const deleteScoringPeriodApi = async (data) => {
  return api.post("hrm/scoring-periods/delete", data).then((res) => {
    return res;
  });
};

// Staff Scoring Criteria API
const createScoringCriteriaApi = async (data) => {
  return api.post("hrm/staff-scoring-criteria/create", data).then((res) => {
    return res;
  });
};

const listScoringCriteriaApi = async (data) => {
  return api.post("hrm/staff-scoring-criteria/list", data || {}).then((res) => {
    return res;
  });
};

const updateScoringCriteriaApi = async (data) => {
  return api.post("hrm/staff-scoring-criteria/update", data).then((res) => {
    return res;
  });
};

const deleteScoringCriteriaApi = async (data) => {
  return api.post("hrm/staff-scoring-criteria/delete", data).then((res) => {
    return res;
  });
};

// Staff Scoring Criteria Rules API
const createScoringRuleApi = async (data) => {
  return api.post("hrm/staff-scoring-criteria/create-rule", data).then((res) => {
    return res;
  });
};

const listScoringRulesApi = async (data) => {
  return api.post("hrm/staff-scoring-criteria/list-rules", data).then((res) => {
    return res;
  });
};

const updateScoringRuleApi = async (data) => {
  return api.post("hrm/staff-scoring-criteria/update-rule", data).then((res) => {
    return res;
  });
};

const deleteScoringRuleApi = async (data) => {
  return api.post("hrm/staff-scoring-criteria/delete-rule", data).then((res) => {
    return res;
  });
};
const ListDailyAttendanceReport = async (data) => {
  return api.post("hrm/staff-attendance/daily-attendance",data).then((res) => {
    return res;
  });
};


const UploadDesignationApi = async (data) => {
  return api.post("hrm/upload-designations", data).then((res) => {
    return res;
  });
};
const UploadEmployeesApi = async (data) => {
  return api.post("hrm/upload-employees", data).then((res) => {
    return res;
  });
};
const UploadOfficesApi = async (data) => {
  return api.post("offices/upload-offices", data).then((res) => {
    return res;
  });
};
const UploadBankAccountsApi = async (data) => {
  return api.post("hrm/bank-accounts/upload", data).then((res) => {
    return res;
  });
};
const UploadBudgetAllocationsApi = async (data) => {
  return api.post("hrm/ba/upload-project-codes", data).then((res) => {
    return res;
  });
};
const BudgetAllocationsApi = async (data) => {
  return api.post("hrm/payroll/reports/budget-codes", data).then((res) => {
    return res;
  });
};
const DeleteBudgetAllocationsApi = async (data) => {
  return api.post("hrm/ba/delete-allocation", data).then((res) => {
    return res;
  });
};


//Assets API
const AssetListApi = async (data) => {
  return api.post("assets/list", data).then((res) => {
    return res;
  });
};
const AssetCategoryListApi = async () => {
  return api.get("assets/category/list").then((res) => {
    return res;
  });
};

const AssetCreateCategoryApi = async (data) => {
  return api.post("assets/category/create", data).then((res) => {
    return res;
  });
};
export const AssetUpdateCategoryApi = async (data) => {
  return api.post("assets/category/update", data).then((res) => {
    return res;
  });
};
const AssetDeleteCategoryApi = async (data) => {
  return api.post("assets/category/delete", data).then((res) => {
    return res;
  });
};

//Assets - Cards APIs
const ListCards = async () => {
  return api.post("cards/list-cards").then((res) => {
    return res;
  });
};

const ListCardsPage = async (url) => {
  // genericPost(url, {})
  return axios.post(url).then((res) => {
    return res;
  });
};

const searchCardHistoryApi = async (data) => {
  return api.post("br/card-history", data).then((res) => {
    return res;
  });
};


const getTemplateByName = async (data) => {
  return api.post("hmis/get-template", data).then((res) => {
    return res;
  });
};
//generic post
const genericPost = async (link,data) => {
  // console.log("LINK: "+link," DATA: "+JSON.stringify(data));
  return api.post(link, data).then((res) => {
    return res;
  });
};


const getDownloadablePayslip = async (url,data) => {
  try {
    const response = await api.post(
     url,
      data,
      {
        responseType: "blob",
      }
    );
    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    return pdfBlob;
  } catch (e) {}
  }

const getList = async (endPoint)=>{
 return await genericGet(endPoint)
  .then((res) => {
    if (res.status == 200) {
      if (res.data.status == SUCCESS_RESULT) {
        let responseData = res.data.data;
      return responseData;
    }  
      } else {
        cogoToast.error(res.data.message, {});
        return [];
      }
  })
  .catch((err) => {
    cogoToast.error(err, {});
 
  
    return [];

  });
} 
const postList = async (endPoint,data)=>{
  return await genericPost(endPoint,data)
   .then((res) => {
     if (res.status == 200) {
       if (res.data.status == SUCCESS_RESULT) {
         let responseData = res.data.data;
       return responseData;
     }  
       } else {
         cogoToast.error(res.data.message, {});
         return [];
       }
   })
   .catch((err) => {
     cogoToast.error(err, {});
  
   
     return [];
 
   });
 } 


// USERS  Management API
const getUsers = async () => {
  return api.get("users/list-all-users").then((res) => {
    // console.log(res)
    return res;
  });
};

const CreateUsers = async (data) => {
  return api.post("users/create-user", data).then((res) => {
    return res;
  });
};

const UpdateAccountStatus = async (data) => {
  return api.post("users/update-account-status", data).then((res) => {
    return res;
  });
};

const UpdateUsers = async (data) => {
  return api.post("users/upload-users", data).then((res) => {
    return res;
  });
};

const updateUserApi = async (data) => {
  return api.post("users/update-user", data).then((res) => {
    return res;
  });
};

const getAuditLogsApi = async (data) => {
  return api.post("users/audit-logs", data).then((res) => {
    return res;
  });
};

const SendPassword = async (data) => {
  return api.post("users/send-password", data).then((res) => {
    // console.log(res)
    return res;
  });
}



//Generic Post File Download
const genericPostFileDownload = async (link,data) => {
  return apiDownloader.post(link, data).then((res) => {
    return res;
  });
};
const genericGet =async (link,data) => {
  return api.get(link, data).then((res) => {
    return res;
  });
};
const genericPostDownload = async (link,data) => {
  // console.log("LINK: "+link," DATA: "+JSON.stringify(data));
  return api.post(link, data).then((res) => {
    return res;
  });
};


const transferCardInventoryApi = async (data) => {
  return api.post("ebms-cards/transfer", data).then((res) => {
    return res;
  });
};

const listCardsByOfficeApi = async (data) => {
  return api.post("ebms-cards/list", data).then((res) => {
    return res;
  });
};

const deleteCardInventoryApi = async (data) => {
  return api.post("ebms-cards/delete", data).then((res) => {
    return res;
  });
};

const createLostCardsApi = async (data) => {
  return api.post("ebms-cards/create-lost-cards", data).then((res) => {
    return res;
  });
};

const createDamagedCardsApi = async (data) => {
  return api.post("ebms-cards/create-damaged-cards", data).then((res) => {
    return res;
  });
};

const createCardInventoryApi = async (data) => {
  return api.post("ebms-cards/create", data).then((res) => {
    return res;
  });
};

const getCardSummaryApi = async () => {
  return api.post("ebms-cards/summary", {}).then((res) => {
    return res;
  });
};

const deleteAssetApi = async (data) => {
  return api.post("assets/delete", data).then((res) => {
    return res;
  });
};
const createAssetApi = async (data) => {
  return api.post("assets/create", data).then((res) => {
    return res;
  });
};
const updateAssetApi = async (data) => {
  return api.post("assets/update", data).then((res) => {
    return res;
  });
};
const uploadAssetsApi = async (data) => {
  return api.post("assets/upload", data).then((res) => {
    return res;
  });
};
const issueAssetApi = async (data) => {
  return api.post("assets/issue", data).then((res) => {
    return res;
  });
};
const listOfficesApi = async () => {
  return api.get("offices/list-offices").then((res) => {
    return res;
  });
};

const listStaffDesignationsApi = async (data) => {
  return api.post("hrm/staff-designations/list", data || {}).then((res) => {
    return res;
  });
};

const bulkUpdateStaffDesignationDepartmentApi = async (data) => {
  return api
    .post("hrm/staff-designations/bulk-update-department", data)
    .then((res) => res);
};

// Sector APIs
const createSectorApi = async (data) => {
  return api.post("hrm/sectors/create", data).then((res) => {
    return res;
  });
};

const listSectorsApi = async (data) => {
  return api.post("hrm/sectors/list", data).then((res) => {
    return res;
  });
};

const updateSectorApi = async (data) => {
  return api.post("hrm/sectors/update", data).then((res) => {
    return res;
  });
};

const deleteSectorApi = async (data) => {
  return api.post("hrm/sectors/delete", data).then((res) => {
    return res;
  });
};

// User Sector Assignments API
const assignUserToSectorApi = async (data) => {
  return api.post("hrm/user-sector-assignments/assign", data).then((res) => {
    return res;
  });
};

const listSectorAssignmentsApi = async (data) => {
  return api.post("hrm/user-sector-assignments/list", data).then((res) => {
    return res;
  });
};

const removeSectorAssignmentApi = async (data) => {
  return api.post("hrm/user-sector-assignments/remove", data).then((res) => {
    return res;
  });
};

// Staff Scoring API
const getScorableStaffApi = async (data) => {
  return api.post("hrm/user-sector-assignments/get-scorable-staff", data).then((res) => {
    return res;
  });
};

const createOrUpdateStaffScoringApi = async (data) => {
  return api.post("hrm/staff-scoring/create-or-update", data).then((res) => {
    return res;
  });
};

const submitStaffScoringApi = async (data) => {
  return api.post("hrm/staff-scoring/submit", data).then((res) => {
    return res;
  });
};

const getRatingsByPeriodApi = async (data) => {
  return api.post("hrm/staff-scoring/get-ratings-by-period", data).then((res) => {
    return res;
  });
};

// Create Project Permissions API
const createProjectPermissionsApi = async (data) => {
  return api.post("rights/create-project-permissions", data).then((res) => {
    return res;
  });
};

// Departments API
const listDepartmentsApi = async (data) => {
  return api.post("hrm/departments/list", data || {}).then((res) => {
    return res;
  });
};

// Workforce Planning API
const createWorkforcePlanningApi = async (data) => {
  return api.post("hrm/workforce-planning/create", data).then((res) => res);
};

const listWorkforcePlanningApi = async (data) => {
  return api.post("hrm/workforce-planning/list", data || {}).then((res) => res);
};

const getWorkforcePlanningDetailsApi = async (data) => {
  return api.post("hrm/workforce-planning/details", data).then((res) => res);
};

const updateWorkforcePlanningApi = async (data) => {
  return api.post("hrm/workforce-planning/update", data).then((res) => res);
};

const updateWorkforcePlanningStatusApi = async (data) => {
  return api.post("hrm/workforce-planning/update-status", data).then((res) => res);
};

const deleteWorkforcePlanningApi = async (data) => {
  return api.post("hrm/workforce-planning/delete", data).then((res) => res);
};

const workforcePlanningReportApi = async (data) => {
  return api.post("hrm/workforce-planning/report", data || {}).then((res) => res);
};

const updateWorkforcePlanningCommitteeDecisionApi = async (data) => {
  return api
    .post("hrm/workforce-planning/update-committee-decision", data)
    .then((res) => res);
};

const updateWorkforcePlanningCdActionApi = async (data) => {
  return api.post("hrm/workforce-planning/cd-action", data).then((res) => res);
};

const createWorkforcePlanningReviewApi = async (data) => {
  return api.post("hrm/workforce-planning/reviews/create", data).then((res) => res);
};

const updateWorkforcePlanningReviewApi = async (data) => {
  return api.post("hrm/workforce-planning/reviews/update", data).then((res) => res);
};

// WASH Projects API
const createWashProjectApi = async (data) => {
  return api.post("wash/projects/create", data).then((res) => res);
};

const listWashProjectsApi = async (data) => {
  return api.post("wash/projects/list", data || {}).then((res) => res);
};

/** Public list for WASH dashboard (no auth / no login redirect). */
const listWashProjectsPublicApi = async (data) => {
  return publicApi.post("wash/projects/list", data || {}).then((res) => res);
};

const getWashProjectDetailsApi = async (data) => {
  return api.post("wash/projects/details", data).then((res) => res);
};

const updateWashProjectApi = async (data) => {
  return api.post("wash/projects/update", data).then((res) => res);
};

const deleteWashProjectApi = async (data) => {
  return api.post("wash/projects/delete", data).then((res) => res);
};

export {
  login_api,
  logout_api,
  auth_me_api,
  forgotPassword_api,
  updatePassword_api,
  listAssignedHospitals,
  listAssignedHospitalRooms,
  searchPatients,
  getTemplate,
  genericPost,
  genericGet,
  getTemplateByName,
  savePatientInformation,
  saveVisit,
  getPatientRequests,
  listHospitalRooms,
  updateConsultationDetails,
  RequestLabTest,
  getProjects,
  getComponents,
  assignProjectComponents,
  initSurvey,
  listSurveys,
  cloneSurvey,
  updateQuestionnaire,
  onboardEnumerators,
  ListSurveyEnumerators,
  UpdateSurveyStatus,
  UpdateEnumeratorsStatus,
  getCycles,
  GetProjectBeneficiaries,
  SaveSurveyManagers,
  getSurveyManagers,
  ListEmployeeApi,
  UploadDesignationApi,
  UploadEmployeesApi,
  UploadOfficesApi,
  AssetListApi,
  AssetCategoryListApi,
  AssetCreateCategoryApi,
  AssetDeleteCategoryApi,
  ListSubmissions,
  getComponentTypes,
  CreateComponents,
  genericPostFileDownload,
  createComponentCycle,
  getList,
  postList,
  UploadBankAccountsApi,
  UploadBudgetAllocationsApi,
  DeleteBudgetAllocationsApi,
  BudgetAllocationsApi,
  getUsers,
  CreateUsers,
  SendPassword,
  UpdateAccountStatus,
  UpdateUsers,
  updateUserApi,
  getAuditLogsApi,
  ListCards,
  ListCardsPage,
  searchCardHistoryApi,
  UpdateLabResult,
 
  listBNFAttendance,
  ListDailyAttendanceReport,
  getDownloadablePayslip,
  transferCardInventoryApi,
  listCardsByOfficeApi,
  deleteCardInventoryApi,
  createLostCardsApi,
  createDamagedCardsApi,
  createCardInventoryApi,
  getCardSummaryApi,deleteAssetApi,createAssetApi,updateAssetApi,
  uploadAssetsApi,issueAssetApi,listOfficesApi,
  createSectorApi,
  listSectorsApi,
  updateSectorApi,
  deleteSectorApi,
  assignUserToSectorApi,
  listSectorAssignmentsApi,
  removeSectorAssignmentApi,
  getScorableStaffApi,
  createOrUpdateStaffScoringApi,
  submitStaffScoringApi,
  getRatingsByPeriodApi,
  createScoringPeriodApi,
  listScoringPeriodsApi,
  updateScoringPeriodApi,
  deleteScoringPeriodApi,
  createProjectPermissionsApi,
  listDepartmentsApi,
  ListStaffLightweightApi,
  ListStaffDetailsApi,
  UpdateStaffStatusApi,
  UpdateStaffJoinedAtApi,
  createStaffFileTypeApi,
  listStaffFileTypesApi,
  getStaffFileTypeDetailsApi,
  updateStaffFileTypeApi,
  deleteStaffFileTypeApi,
  uploadStaffFileApi,
  listStaffFilesApi,
  downloadStaffFileApi,
  deleteStaffFileApi,
  listStaffPayslipsApi,
  createScoringCriteriaApi,
  listScoringCriteriaApi,
  updateScoringCriteriaApi,
  deleteScoringCriteriaApi,
  createScoringRuleApi,
  listScoringRulesApi,
  updateScoringRuleApi,
  deleteScoringRuleApi,
  listServiceAccountsApi,
  createServiceAccountApi,
  regenerateServiceAccountSecretApi,
  createWorkforcePlanningApi,
  listWorkforcePlanningApi,
  getWorkforcePlanningDetailsApi,
  updateWorkforcePlanningApi,
  updateWorkforcePlanningStatusApi,
  deleteWorkforcePlanningApi,
  workforcePlanningReportApi,
  updateWorkforcePlanningCommitteeDecisionApi,
  updateWorkforcePlanningCdActionApi,
  createWorkforcePlanningReviewApi,
  updateWorkforcePlanningReviewApi,
  createWashProjectApi,
  listWashProjectsApi,
  listWashProjectsPublicApi,
  getWashProjectDetailsApi,
  updateWashProjectApi,
  deleteWashProjectApi,
  listStaffDesignationsApi,
  bulkUpdateStaffDesignationDepartmentApi,
  updateComponentProjectApi,
};